/**
 * Dialog de búsqueda instantánea. Debounce + render de snippet con <mark>.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search, FileText, Hash, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useSearch } from '@/features/content/queries';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function SearchDialog({ open, onOpenChange }: Props) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 150);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebounced('');
      setActive(0);
    }
  }, [open]);

  const { data, isFetching } = useSearch(debounced, open);
  const results = data?.results ?? [];

  useEffect(() => {
    setActive(0);
  }, [debounced]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[active]) {
      e.preventDefault();
      navigate({ to: results[active].url });
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 p-0">
        <DialogTitle className="sr-only">Buscar</DialogTitle>
        <div className="flex items-center border-b px-4">
          <Search className="text-muted-foreground mr-2 h-4 w-4 shrink-0" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Buscar artículos, procedimientos, glosario…"
            className="h-12 border-0 px-0 text-base shadow-none focus-visible:ring-0"
          />
          {isFetching && <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />}
        </div>

        <div className="max-h-[420px] overflow-y-auto p-2">
          {debounced.trim().length < 2 ? (
            <div className="text-muted-foreground py-12 text-center text-sm">
              Escribe al menos 2 caracteres para buscar.
            </div>
          ) : results.length === 0 && !isFetching ? (
            <div className="text-muted-foreground py-12 text-center text-sm">
              Sin resultados para “{debounced}”.
            </div>
          ) : (
            <ul>
              {results.map((r, i) => (
                <li key={`${r.kind}-${r.refId}`}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => {
                      navigate({ to: r.url });
                      onOpenChange(false);
                    }}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors',
                      active === i ? 'bg-accent' : 'hover:bg-accent/50',
                    )}
                  >
                    {r.kind === 'article' ? (
                      <FileText className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                    ) : (
                      <Hash className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{r.titulo}</div>
                      <div
                        className="text-muted-foreground line-clamp-2 text-xs"
                        // El snippet viene con <mark> sanitizado del backend
                        dangerouslySetInnerHTML={{ __html: r.snippet }}
                      />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
